import { Checkbox, Divider, Form, FormField, Header, Segment } from 'semantic-ui-react'
import { UltyDropdown } from '@ulty/ulty-ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { patchDevice, patchDeviceUserOptions } from '../../../services/Device/Device.service'
import { useDeviceInfo } from '../../../devices/device.provider'
import { ultyToast } from '../../Shared/UltyToast/UltyToast'
import useUser from '../../Shared/UserProvider/useUser'

const SUBSCRIPTION_TYPES = {
    ORDER_NOTIFICATIONS: 'ORDER_NOTIFICATIONS',
    BUSY_NOTIFICATIONS: 'BUSY_NOTIFICATIONS',
}

const DeviceOptions = ({device}) => {
    const [t] = useTranslation()
    const {user} = useUser()
    const queryClient = useQueryClient()
    const {setDeviceData} = useDeviceInfo()

    const patchDeviceMutation = useMutation({
        mutationFn: (patch) => {
            return patchDevice(device.id, patch)
        },
        onSuccess: async ({patchedDevice}) => {
            if (patchedDevice) {
                setDeviceData(prev => ({...prev, ...patchedDevice}))
            }
            await queryClient.invalidateQueries({queryKey: ['devices', device.id]})
            ultyToast({type: 'success', title: t('global.registerSuccess')})
        },
        onError: () => {
            ultyToast({type: 'error', title: t('global.anErrorOccurred')})
        },
    })

    const patchDeviceUserOptionsMutation = useMutation({
        mutationFn: ({subscribedTo}) => {
            console.log('subscribedTo', subscribedTo)
            return patchDeviceUserOptions(device.id, device.user.id, {subscribedTo})
        },
        onMutate: async ({subscribedTo}) => {
            await queryClient.cancelQueries({queryKey: ['devices', device.id]})
            const previousDevice = queryClient.getQueryData(['devices', device.id])

            queryClient.setQueryData(['devices', device.id], (prev) => {
                if (!prev) return prev
                return {
                    ...prev,
                    user: {
                        ...prev.user,
                        subscribedTo,
                    },
                }
            })

            return {previousDevice}
        },
        onSuccess: () => {
            ultyToast({type: 'success', title: t('global.registerSuccess')})
        },
        onError: (err, variables, context) => {
            if (context?.previousDevice) {
                queryClient.setQueryData(['devices', device.id], context.previousDevice)
            }
            ultyToast({type: 'error', title: t('global.anErrorOccurred')})
        },
        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: ['devices', device.id]})
        },
    })

    const handleSubscriptionChange = (addOrRemove, subscriptionType) => {
        let subscriptions = device.user.subscribedTo
        subscriptions = addOrRemove === 'ADD'
          ? [...subscriptions, subscriptionType]
          : subscriptions.filter(subscription => subscription !== subscriptionType)

        patchDeviceUserOptionsMutation.mutate({
            subscribedTo: subscriptions,
        })
    }

    return (
      <Segment>
          <Header>{t('administration.device.notifications.notifications')}</Header>
          <Form>
              {user.isStaff && (
                <FormField>
                    <Checkbox
                      toggle
                      label={t('administration.device.notifications.order_notifications')}
                      checked={device.user.subscribedTo?.includes(SUBSCRIPTION_TYPES.ORDER_NOTIFICATIONS)}
                      disabled={user.provider.role.name === 'ORDER_PREPARER'}
                      onChange={(evt, {checked}) => handleSubscriptionChange(checked ? 'ADD' : 'REMOVE', SUBSCRIPTION_TYPES.ORDER_NOTIFICATIONS)}
                    />
                </FormField>
              )}
              <UltyDropdown
                label={t('administration.device.notifications.duration')}
                options={[
                    {key: 'short', text: t('administration.device.notifications.short'), value: 'SHORT'},
                    {key: 'long', text: t('administration.device.notifications.long'), value: 'LONG'},
                ]}
                multiple={false}
                loading={patchDeviceMutation.isPending}
                onChange={(e, {value}) => {
                    patchDeviceMutation.mutate({
                        soundMode: value,
                    })
                }}
                value={device.soundMode}
              />
          </Form>

          {user.isStaff && window.Android?.canHandleSecondaryNotifications?.() && (
            <>
                <Divider/>
                <FormField>
                    <Checkbox
                      toggle
                      label={t('administration.device.notifications.busy_notifications')}
                      checked={device.user.subscribedTo?.includes('BUSY_NOTIFICATIONS')}
                      onChange={(evt, {checked}) => handleSubscriptionChange(checked ? 'ADD' : 'REMOVE', SUBSCRIPTION_TYPES.BUSY_NOTIFICATIONS)}
                    />
                </FormField>
            </>
          )}
      </Segment>
    )
}

export { DeviceOptions }
